import React from "react";
import { Rocket, Lightbulb, HandHeart, Users, Building2, GraduationCap, PiggyBank, ClipboardCheck, ShieldCheck, CircleDollarSign, Briefcase, CheckCircle } from 'lucide-react';
import Header from "./Header";
import Footer from "./Footer";
import MobileFooter from './MobileFooter';
import { Card, Heading, Eyebrow, Container } from '../ui';

const About = () => {
  return (
    <div className="min-h-screen bg-bg pb-20 lg:pb-0 font-sans">
      <Header />

      {/* Hero Section */}
      <div className="on-dark bg-navy py-12 sm:py-16">
        <Container>
          <div className="text-center">
            {/* Icon above heading - same as HomePage */}
            <div className="flex items-center justify-center mb-4 sm:mb-5">
              <div className="p-3 sm:p-4 rounded-lg glass">
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
            </div>
            <Eyebrow className="justify-center">About KBC</Eyebrow>
            <Heading variant="display" className="mt-3">
              About Us <em className="kbc-serif">Kongu</em> Business Community (KBC)
            </Heading>
            <p className="mt-4 text-text text-base sm:text-lg">
              Empowering Kongu through business, collaboration, and innovation
            </p>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="max-w-6xl py-10 sm:py-14 md:py-20">
        {/* Who We Are */}
        <div className="mb-16 text-left">
          <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <Heading variant="section" className="leading-tight">
              Who We <em className="kbc-serif">Are</em>
            </Heading>
          </div>
          <p className="text-text text-sm sm:text-base leading-relaxed">
            The Kongu Business Community (KBC) is a collective of entrepreneurs, professionals, and visionaries united by a single purpose – to empower Kongu through business, collaboration, and innovation. Rooted in tradition and driven by modern enterprise, KBC is not just a network, but a movement that brings together Konguprenuers to grow, connect, and create extraordinary outcomes.
          </p>
        </div>

        {/* Vision */}
        <div className="mb-12 text-left">
          <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-info/10 text-info flex items-center justify-center">
              <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <Heading variant="section" className="leading-tight">
              Our <em className="kbc-serif">Vision</em>
            </Heading>
          </div>
          <p className="text-text text-sm sm:text-base leading-relaxed">
            To build the most trusted entrepreneurial ecosystem that nurtures the spirit of innovation, strengthens community bonds, and positions Kongu as a hub for sustainable and scalable businesses.
          </p>
        </div>

        {/* Mission */}
        <div className="mb-12 text-left">
          <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Rocket className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <Heading variant="section" className="leading-tight">
              Our <em className="kbc-serif">Mission</em>
            </Heading>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <p className="text-text text-sm sm:text-base leading-relaxed">Connect Kongu entrepreneurs across industries, geographies, and generations.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <p className="text-text text-sm sm:text-base leading-relaxed">Collaborate by sharing resources, expertise, and opportunities.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <p className="text-text text-sm sm:text-base leading-relaxed">Create lasting impact by mentoring, funding, and scaling businesses.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <p className="text-text text-sm sm:text-base leading-relaxed">Celebrate the spirit of Konguprenuers who blend heritage with progress.</p>
            </div>
          </div>
        </div>

        {/* What We Do */}
        <div className="mb-12 text-left">
          <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
              <Briefcase className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <Heading variant="section" className="leading-tight">
              What We <em className="kbc-serif">Do</em>
            </Heading>
          </div>
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            <Card interactive padding="lg" className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-primary" />
                <h3 className="text-base sm:text-lg font-semibold text-ink font-display">Business Directory</h3>
              </div>
              <p className="text-text text-sm sm:text-base">A one-stop digital platform that showcases every Kongu business, making them discoverable and accessible.</p>
            </Card>
            <Card interactive padding="lg" className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-info" />
                <h3 className="text-base sm:text-lg font-semibold text-ink font-display">Networking & Conclaves</h3>
              </div>
              <p className="text-text text-sm sm:text-base">Regular events, industrial visits, and forums where leaders and aspiring entrepreneurs exchange knowledge and opportunities.</p>
            </Card>
            <Card interactive padding="lg" className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <GraduationCap className="w-5 h-5 text-warning" />
                <h3 className="text-base sm:text-lg font-semibold text-ink font-display">Mentorship & Funding</h3>
              </div>
              <p className="text-text text-sm sm:text-base">Pairing young entrepreneurs with seasoned industry leaders and connecting them with investors.</p>
            </Card>
            <Card interactive padding="lg" className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <HandHeart className="w-5 h-5 text-accent" />
                <h3 className="text-base sm:text-lg font-semibold text-ink font-display">Community Initiatives</h3>
              </div>
              <p className="text-text text-sm sm:text-base">Scholarships, support programs, and activities that uplift the next generation of innovators.</p>
            </Card>
          </div>
        </div>

        {/* Governance */}
        <div className="mb-16 text-left">
          <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-success/10 text-success flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <Heading variant="section" className="leading-tight">
              Our <em className="kbc-serif">Governance</em>
            </Heading>
          </div>
          <Card padding="lg" className="text-left">
            <p className="text-text text-sm sm:text-base leading-relaxed mb-4">
              KBC is registered as a Section 8 company under the Companies Act, 2013 – a not-for-profit entity formed to promote commerce, trade, education, and social welfare. Being a Section 8 company ensures that our mission remains community-centric, transparent, and impact-driven, with all surpluses reinvested into building opportunities for entrepreneurs and society.
            </p>
            <p className="text-text text-sm sm:text-base leading-relaxed mb-4">
              KBC is a community-driven organization governed through an active board of directors selected and evaluated on clear KPIs (Key Performance Indicators).
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-text text-sm sm:text-base leading-relaxed">The community is not owned or managed by any single individual or private group.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-text text-sm sm:text-base leading-relaxed">Collective decision-making power rests with the board, which functions transparently and is accountable to the community through performance-based governance.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-text text-sm sm:text-base leading-relaxed">This governance model ensures fairness, continuity, and sustainability, keeping the focus on community growth rather than personal interest.</p>
              </div>
            </div>
          </Card>
        </div>
      </Container>

      <Footer />
      <MobileFooter />
    </div>
  );
};

export default About;
